import { FACEBOOK, TWITTER, LINKEDIN, LINK } from 'src/constants/socialShare';
import type { Router } from 'src/constants/urls';
import type { PageConfig } from 'src/libs/PageConfig';
import { downloadFile } from 'src/libs/fileDownload';
import configurationService from 'src/services/configurationService';
import type { EmployerSiteNames } from 'src/siteConfig';
import {
  trackJobInfoClickEvent,
  trackViewAdClickEvent,
  trackEditAdClickEvent,
  trackViewJobAdInformationClickEvent,
  trackRefreshAdClickEvent,
  trackCopyAdClickEvent,
  trackExportToCsvClickEvent,
  trackTalentSearchLinkOutClickEvent,
  trackShareJobOnFacebookEvent,
  trackShareJobOnLinkedInEvent,
  trackShareJobOnTwitterEvent,
} from 'src/utils/events';
import handleClickSocialShareLink from 'src/utils/handleClickSocialShareLink';

import {
  EVENT_NAME_VIEW_JOB_DETAILS_ACTIVE_JOB_PRESSED,
  EVENT_NAME_VIEW_JOB_DETAILS_INACTIVE_JOB_PRESSED,
} from '../../../constants/pageTrackingData';
import type { IJob } from '../../../stores/JobStore/Job';

import { navigateToUrl } from './utils';

export enum JobAction {
  VIEW = 'view',
  TALENT_SEARCH = 'talentSearch',
  VIEW_AD_INFORMATION = 'viewAdInformation',
  EDIT = 'edit',
  SHARE_ON_FACEBOOK = 'shareOnFacebook',
  SHARE_ON_LINKEDIN = 'shareOnLinkedIn',
  SHARE_ON_X = 'shareOnX',
  COPY_LINK_TO_JOB = 'copyLinkToJob',
  EXPORT_CANDIDATE_LIST = 'exportCandidateList',
  REFRESH = 'refresh',
  COPY = 'copy',
  EXPIRE = 'expire',
  NOTIFICATION_PREFERENCE = 'notificationPreference',
  TEAM_MEMBER_ACCESS = 'teamMemberAccess',
}

export const getNumberOfApplicationsInBucket = (job: IJob) =>
  job.jobApplicationStore.currentTotalOfJobApplications;

const viewJobAd = (jobId: number, job: IJob, router: Router) => {
  trackJobInfoClickEvent(
    job.isActive
      ? EVENT_NAME_VIEW_JOB_DETAILS_ACTIVE_JOB_PRESSED
      : EVENT_NAME_VIEW_JOB_DETAILS_INACTIVE_JOB_PRESSED,
    jobId,
  );
  trackViewAdClickEvent({
    prospectInBucketCount: getNumberOfApplicationsInBucket(job),
  });
  navigateToUrl(
    router.viewJobAd({
      jobStatus: job.jobInformation.status,
      jobId,
      ref: 'cm-ui',
    }),
    true,
  );
};

const viewJobAdInformation = (jobId: number, job: IJob) => {
  trackViewJobAdInformationClickEvent({
    prospectInBucketCount: getNumberOfApplicationsInBucket(job),
  });
};

const editJobAd = (jobId: number, job: IJob, router: Router) => {
  trackEditAdClickEvent({
    prospectInBucketCount: getNumberOfApplicationsInBucket(job),
  });
  navigateToUrl(router.editJobAd({ jobId }));
};

const exportCandidateList = (
  jobId: number,
  job: IJob,
  _router: Router,
  addCustomError: PageConfig['dataDogRum']['addCustomError'],
) => {
  const applicationsExportUrl = configurationService.getRelativeUrl(
    '/attachment/applications/export',
  );

  const searchParams = new URLSearchParams();
  searchParams.append('jobId', jobId.toString());

  downloadFile(
    `${applicationsExportUrl}?${searchParams.toString()}`,
    () => {
      trackExportToCsvClickEvent({
        prospectInBucketCount: getNumberOfApplicationsInBucket(job),
      });
    },
    (error) => {
      addCustomError({
        error,
        context: {
          activity: 'EXPORT_TO_CSV',
          description: 'Error attempting: exportApplicationsToCsv',
          jobId,
        },
      });
    },
  );
};

const refreshJobAd = (jobId: number, job: IJob, router: Router) => {
  trackRefreshAdClickEvent({
    prospectInBucketCount: getNumberOfApplicationsInBucket(job),
  });
  navigateToUrl(router.refreshJobAd({ jobId }));
};

const copyJobAd = (jobId: number, job: IJob, router: Router) => {
  trackCopyAdClickEvent({
    prospectInBucketCount: getNumberOfApplicationsInBucket(job),
  });
  navigateToUrl(router.copyJobAd({ jobId }));
};

const trackTalentSearchExitLinkClick = () => {
  trackTalentSearchLinkOutClickEvent({
    isExitLink: '1',
  });
};

/**
 * @deprecated We should control tracking from above instead of baking it into child components
 */
export const triggerJobAction = (
  jobId: number,
  jobAction: JobAction,
  job: IJob,
  router: Router,
  site: EmployerSiteNames,
  addCustomError: PageConfig['dataDogRum']['addCustomError'],
) => {
  const getJobAdUrl = (ref: string) =>
    router.viewJobAd({
      jobStatus: job.jobInformation.status,
      jobId: job.jobInformation.jobId ?? 0,
      ref,
    });

  const actionMapper: Record<
    JobAction,
    (
      jobId: number,
      job: IJob,
      router: Router,
      addCustomError: PageConfig['dataDogRum']['addCustomError'],
    ) => void
  > = {
    [JobAction.VIEW]: viewJobAd,
    [JobAction.TALENT_SEARCH]: trackTalentSearchExitLinkClick,
    [JobAction.VIEW_AD_INFORMATION]: viewJobAdInformation,
    [JobAction.EDIT]: editJobAd,
    [JobAction.EXPORT_CANDIDATE_LIST]: exportCandidateList,
    [JobAction.REFRESH]: refreshJobAd,
    [JobAction.COPY]: copyJobAd,
    [JobAction.EXPIRE]: () => {},
    [JobAction.NOTIFICATION_PREFERENCE]: () => {},
    [JobAction.TEAM_MEMBER_ACCESS]: () => {},
    [JobAction.SHARE_ON_FACEBOOK]: () => {
      handleClickSocialShareLink({
        option: FACEBOOK,
        jobAdUrl: getJobAdUrl('fb-share-cm-ui'),
        title: job.jobInformation.title,
        site,
      });
      trackShareJobOnFacebookEvent();
    },
    [JobAction.SHARE_ON_X]: () => {
      handleClickSocialShareLink({
        option: TWITTER,
        jobAdUrl: getJobAdUrl('twitter-share-cm-ui'),
        title: job.jobInformation.title,
        site,
      });
      trackShareJobOnTwitterEvent();
    },
    [JobAction.SHARE_ON_LINKEDIN]: () => {
      handleClickSocialShareLink({
        option: LINKEDIN,
        jobAdUrl: getJobAdUrl('linkedin-share-cm-ui'),
        title: job.jobInformation.title,
        site,
      });
      trackShareJobOnLinkedInEvent();
    },
    [JobAction.COPY_LINK_TO_JOB]: () => {
      handleClickSocialShareLink({
        option: LINK,
        jobAdUrl: getJobAdUrl('direct-share-cm-ui'),
        title: job.jobInformation.title,
        site,
      });
      trackCopyAdClickEvent();
    },
  };
  const action = actionMapper[jobAction];
  action(jobId, job, router, addCustomError);
};
